import { assertDraftRequest } from './types.js';

const ENDPOINTS = {
  analyzeMirror: 'mirror',
  analyzeMeter: 'meter',
  analyzeMessageFindings: 'message-findings',
  scoreDraft: 'draft',
  generateReply: 'chat',
};

function nowIso() {
  return new Date().toISOString();
}

function nowMs() {
  try {
    if (globalThis.performance?.now) return globalThis.performance.now();
  } catch {
    // Fall through to Date.now.
  }
  return Date.now();
}

function clonePayload(value) {
  try {
    if (globalThis.structuredClone) return globalThis.structuredClone(value);
  } catch {
    // Fall through to JSON cloning.
  }
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return value;
  }
}

function errorPayload(err) {
  if (!err || typeof err !== 'object') return { message: String(err) };
  return {
    name: err.name ?? 'Error',
    message: err.message ?? String(err),
    stack: err.stack ?? null,
  };
}

function callOnLog(onLog, row) {
  try {
    onLog?.(row);
  } catch {
    // A telemetry observer must never affect inference behavior.
  }
}

function requestMessageId(request) {
  return request?.msgId ?? request?.msg_id ?? request?.messageId ?? request?.newMessage?.id ?? null;
}

async function observeCall(method, args, invoke, onLog, scoring) {
  const request = args[0] ?? {};
  if (method === 'scoreDraft') assertDraftRequest(request);
  const requestedAt = nowIso();
  const started = nowMs();
  const base = {
    endpoint: ENDPOINTS[method],
    conversation_id: request.conversationId ?? null,
    msg_id: requestMessageId(request),
    requested_at: requestedAt,
    request_payload: clonePayload(scoring ? { ...request, scoring } : request),
  };

  try {
    const response = await invoke();
    callOnLog(onLog, {
      ...base,
      response_payload: clonePayload(response),
      error: null,
      latency_ms: Math.max(0, nowMs() - started),
    });
    return response;
  } catch (err) {
    callOnLog(onLog, {
      ...base,
      response_payload: null,
      error: errorPayload(err),
      latency_ms: Math.max(0, nowMs() - started),
    });
    throw err;
  }
}

export function observeClient(client, onLog) {
  if (!onLog) return client;
  const observed = {
    analyzeMirror(...args) {
      return observeCall('analyzeMirror', args, () => client.analyzeMirror(...args), onLog);
    },
    analyzeMeter(...args) {
      return observeCall('analyzeMeter', args, () => client.analyzeMeter(...args), onLog);
    },
    analyzeMessageFindings(...args) {
      return observeCall(
        'analyzeMessageFindings',
        args,
        () => client.analyzeMessageFindings(...args),
        onLog
      );
    },
    scoreDraft(request, scoring) {
      if (client.isLocalOnly?.('scoreDraft')) return client.scoreDraft(request);
      return observeCall('scoreDraft', [request], () => client.scoreDraft(request), onLog, scoring);
    },
    generateReply(...args) {
      return observeCall('generateReply', args, () => client.generateReply(...args), onLog);
    },
  };

  return new Proxy(client, {
    get(target, prop, receiver) {
      if (
        Object.prototype.hasOwnProperty.call(observed, prop) &&
        typeof target[prop] === 'function'
      ) {
        return observed[prop];
      }
      return Reflect.get(target, prop, receiver);
    },
  });
}
